import PropTypes from 'prop-types';
import { useFieldArray, useWatch } from 'react-hook-form';
import { PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import {
  ARRIVAL_SCHEDULE_LINES_TIP,
} from '#/modules/srmMgmt/purchaseOrder/schemas/arrivalScheduleSchema';
import { formatDisplayQty } from '#/utils/formatNumber';

const LINE_GRID_COLS =
  '@3xl/form-section:grid-cols-[minmax(10rem,1fr)_minmax(0,8rem)_minmax(0,8rem)_2.5rem]';

const LINES_MIN_W = cn(
  'w-full',
  '@3xl/form-section:min-w-[36rem]',
);

const LINE_HEADER_CLASS = cn(
  'sticky top-0 z-10 hidden w-full gap-3 border-b border-gray-300 bg-gray-100 px-3 py-2',
  'text-xs font-medium text-gray-600',
  '@3xl/form-section:grid',
  LINE_GRID_COLS,
);

const LINE_ROW_CLASS = cn(
  'grid w-full grid-cols-1 gap-3 px-3 py-3',
  '@3xl/form-section:items-start',
  LINE_GRID_COLS,
);

const ArrivalScheduleLineRow = ({
  index,
  register,
  errors,
  line,
  onRemove,
  canRemove,
}) => (
  <li className={LINE_ROW_CLASS}>
    <div className="min-w-0">
      <p className="@3xl/form-section:hidden text-xs text-gray-500">物料</p>
      <MaterialNameStack name={line.mtrName} code={line.mtrNo} />
      <input type="hidden" {...register(`lines.${index}.mtrId`)} />
    </div>
    <div className="min-w-0">
      <p className="@3xl/form-section:hidden text-xs text-gray-500">未排程量</p>
      <p className="pt-2 text-sm tabular-nums text-gray-700">
        {formatDisplayQty(line.unscheduledQty)}
      </p>
    </div>
    <div className="min-w-0">
      <FloatingInput
        id={`lines.${index}.plannedQty`}
        label="計劃量"
        type="number"
        min={0}
        step="any"
        register={register}
        errors={errors}
        containerClassName="w-full"
      />
    </div>
    <div className="flex min-w-0 @3xl/form-section:justify-end @3xl/form-section:pt-2">
      {canRemove ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
          aria-label={`移除明細 ${index + 1}`}
          onClick={() => onRemove(index)}
        >
          <PiTrashBold className="size-4" />
        </Button>
      ) : null}
    </div>
  </li>
);

ArrivalScheduleLineRow.propTypes = {
  index: PropTypes.number.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  line: PropTypes.shape({
    mtrId: PropTypes.string,
    mtrNo: PropTypes.string,
    mtrName: PropTypes.string,
    unscheduledQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    plannedQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onRemove: PropTypes.func.isRequired,
  canRemove: PropTypes.bool,
};

const ArrivalScheduleLinesEditor = ({
  control,
  register,
  errors,
}) => {
  const { fields, remove } = useFieldArray({
    control,
    name: 'lines',
  });
  const watchedLines = useWatch({ control, name: 'lines' }) ?? [];

  return (
    <section className="@container/form-section pb-6">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="form-block__title mb-0!">排程明細</h2>
        <p className="text-xs text-muted-foreground">{ARRIVAL_SCHEDULE_LINES_TIP}</p>
      </div>

      {errors?.lines?.message || errors?.lines?.root?.message ? (
        <p className="mb-2 text-sm text-destructive">
          {errors.lines.message || errors.lines.root.message}
        </p>
      ) : null}

      <div className={cn('overflow-x-auto rounded-md border border-gray-300', LINES_MIN_W)}>
        <div className={LINE_HEADER_CLASS}>
          <span>物料</span>
          <span>未排程量</span>
          <span>計劃量</span>
          <span className="sr-only">操作</span>
        </div>
        <ul className="divide-y divide-gray-200">
          {fields.map((field, index) => (
            <ArrivalScheduleLineRow
              key={field.id}
              index={index}
              register={register}
              errors={errors}
              line={watchedLines[index] ?? field}
              onRemove={remove}
              canRemove={fields.length > 1}
            />
          ))}
        </ul>
      </div>
    </section>
  );
};

ArrivalScheduleLinesEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
};

export default ArrivalScheduleLinesEditor;
